import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null); // { link } | true

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      // Backend returns { success, reset_link, token } when the account exists (dev mode)
      setSent(data.reset_link ? { link: data.reset_link } : true);
      toast.success("If an account exists, a reset link has been generated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="forgot-page">
      <div className="w-full max-w-md fade-up">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back to sign in
        </Link>
        <p className="mt-8 text-xs tracking-[0.3em] uppercase text-muted-foreground">Reset access</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight">Forgot your password?</h1>
        <p className="mt-3 text-muted-foreground">
          Enter your account email and we'll generate a secure reset link.
        </p>

        {sent ? (
          <div className="mt-10 border border-border bg-white p-6" data-testid="forgot-result">
            <p className="text-sm">If an account matches that email, a reset link has been created.</p>
            {sent.link && (
              <>
                <p className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">Dev link (email delivery not configured)</p>
                <a
                  href={sent.link}
                  data-testid="reset-link"
                  className="mt-2 block break-all text-primary hover:underline text-sm"
                >
                  {sent.link}
                </a>
              </>
            )}
            <div className="mt-6">
              <Link to="/login" className="text-primary text-sm hover:underline underline-offset-4">
                Return to sign in →
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-6">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input
                id="email"
                data-testid="forgot-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 rounded-none border-0 border-b-2 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-base"
                placeholder="you@royalwowmeals.com"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="forgot-submit-btn"
              className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {busy ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
